import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useLazySectionedPagination from './lazy-sectioned-pagination'
import ListingSectionWithMetadata from '../transports/listing-section-with-metadata-transport'
import { ListingSectionObject } from '../transports/common/listing-section-transport'
import {
    PAGINATION,
    getMergeKey,
    SECTION_MERGE_KEYS,
    SECTION_DISPLAY_ORDER,
    DataSource,
    SectionType,
} from '../constants/search-result-listing'
import { useABEffect } from './ab-effect'
import { SRLNoResultTransport } from '../transports/srl-objects-response-transport'

/** The two data sources currently active in the hybrid listings flow */

export interface MergedSection {
    key: SECTION_MERGE_KEYS
    heading: string
    items: ListingSectionObject[]
    totalCount: number
    source: DataSource
}

interface HybridListingsResult {
    sections: MergedSection[]
    primaryTotalCount: number
    primaryHeading: string
    loading: boolean
    isInitializing: boolean
    hasNextPage: boolean
    loadMore: () => Promise<void>
    reset: () => void
    currentSource: DataSource
    error: Error | null
    srlNoResult?: SRLNoResultTransport
}

interface FetchSectionPageResult {
    sections: ListingSectionWithMetadata[]
    totalItems: number
    primaryHeading?: string
    srlNoResult?: SRLNoResultTransport
}

// Constants
const { ITEMS_PER_PAGE } = PAGINATION

/**
 * Hook to manage hybrid listings from multiple sources (Main, Holidu).
 * It handles pagination for each source sequentially and merges the results into unified sections.
 */
function useHybridListings(
    fetchMainPage: (page: number) => Promise<FetchSectionPageResult>,
    fetchHoliduPage: (page: number) => Promise<FetchSectionPageResult>,
    filter: string,
): HybridListingsResult {
    const [currentSource, setCurrentSource] = useState<DataSource>(DataSource.MAIN)
    const primaryHeadingRef = useRef<string>('')
    const srlNoResultRef = useRef<SRLNoResultTransport | undefined>(undefined)

    // Wrap fetchMainPage to capture primaryHeading from the first page
    const wrappedFetchMainPage = useCallback(
        async (page: number) => {
            const result = await fetchMainPage(page)
            if (page === 1) {
                if (result.primaryHeading) {
                    primaryHeadingRef.current = result.primaryHeading
                }
                if (result.srlNoResult) {
                    srlNoResultRef.current = result.srlNoResult
                }
            }
            return result
        },
        [fetchMainPage],
    )

    // Initialize pagination hooks for each source
    // Auto-fetch is disabled because we control the flow manually via Virtuoso and auto-switching
    const mainPagination = useLazySectionedPagination(wrappedFetchMainPage, ITEMS_PER_PAGE, false)
    const holiduPagination = useLazySectionedPagination(fetchHoliduPage, ITEMS_PER_PAGE, false)

    // Map source to its pagination instance for easy access
    const paginations = useMemo(
        () =>
            ({
                [DataSource.MAIN]: mainPagination,
                [DataSource.HOLIDU]: holiduPagination,
            }) as Record<DataSource, typeof mainPagination>,
        [mainPagination, holiduPagination],
    )

    const isLoadingRef = useRef(false)
    const initializedRef = useRef(false)
    const committedFilterRef = useRef(filter)
    const currentSourceRef = useRef<DataSource>(currentSource)
    const paginationsRef = useRef<Record<DataSource, typeof mainPagination>>(paginations)
    paginationsRef.current = paginations

    const autoSwitchUsedRef = useRef<Record<DataSource, boolean>>({
        [DataSource.MAIN]: false,
        [DataSource.HOLIDU]: false,
    })

    /**
     * Initial fetch for the main source on mount
     */
    // Initial fetch effect removed to avoid conflict with reset effect

    /**
     * Auto-switch logic:
     * When one source is exhausted (no next page) AND yielded no results (empty sections),
     * automatically switch to the next source to avoid showing a blank screen or "Load More" button prematurely.
     */
    useABEffect(() => {
        if (committedFilterRef.current !== filter) {
            return
        }

        const checkAndSwitch = (source: DataSource, pagination: typeof mainPagination, nextSource: DataSource) => {
            const isExhausted = pagination.currentPage >= 1 && !pagination.hasNextPage
            const isEmpty = pagination.allSections.length === 0

            if (currentSource === source && isExhausted && isEmpty && !autoSwitchUsedRef.current[nextSource]) {
                autoSwitchUsedRef.current[nextSource] = true
                currentSourceRef.current = nextSource
                setCurrentSource(nextSource)
                paginations[nextSource].nextPage()
                return true
            }
            return false
        }

        // Try switch Main -> Holidu
        if (checkAndSwitch(DataSource.MAIN, mainPagination, DataSource.HOLIDU)) {
            return
        }
    }, [currentSource, mainPagination, holiduPagination, paginations, filter])

    /**
     * Handles loading more items.
     * Switches sources if the current one is exhausted.
     */
    const loadMore = useCallback(async () => {
        if (isLoadingRef.current) {
            return
        }

        // Read from ref to get the latest source (not the stale closure value)
        const source = currentSourceRef.current
        const currentPagination = paginationsRef.current[source]

        isLoadingRef.current = true

        try {
            if (currentPagination.hasNextPage) {
                await currentPagination.nextPage()
            } else {
                // Current source exhausted, try to switch
                if (source === DataSource.MAIN) {
                    currentSourceRef.current = DataSource.HOLIDU
                    setCurrentSource(DataSource.HOLIDU)
                    await paginationsRef.current[DataSource.HOLIDU].nextPage()
                } else {
                    // Holidu (last source) exhausted, nothing more to do
                }
            }
        } finally {
            isLoadingRef.current = false
        }
    }, [])

    /**
     * Merges sections from all sources into a single ordered list
     */
    const sections = useMemo(() => {
        const allSections = [...mainPagination.allSections, ...holiduPagination.allSections]

        // Group by merge key (type + source)
        const sectionsMap = new Map<SECTION_MERGE_KEYS, MergedSection>()

        allSections.forEach(section => {
            const mergeKey = getMergeKey(section.type as SectionType, section.source as DataSource)

            if (!sectionsMap.has(mergeKey)) {
                sectionsMap.set(mergeKey, {
                    key: mergeKey,
                    heading: section.heading || '',
                    items: [],
                    totalCount: 0,
                    source: section.source as DataSource,
                })
            }

            const existing = sectionsMap.get(mergeKey)!
            existing.items.push(...section.objects)
            // Use the latest totalCount available
            if (section.totalCount !== undefined) {
                existing.totalCount = section.totalCount
            }
        })

        // Sort by predefined display order
        const orderedSections = SECTION_DISPLAY_ORDER.map(key => sectionsMap.get(key)).filter(
            (s): s is MergedSection => s !== undefined,
        )

        return orderedSections
    }, [mainPagination.allSections, holiduPagination.allSections])

    // Derived state
    const primaryTotalCount = sections.find(s => s.key === SECTION_MERGE_KEYS.PRIMARY_MAIN)?.totalCount ?? 0

    const loading = paginations[currentSource].loading

    // Track if initial data has loaded
    // Keep initializing state while we have no data and are still trying sources
    const hasAnyData = sections.some(s => s.items.length > 0)
    const isInitializing =
        (mainPagination.currentPage === 0 && (mainPagination.loading || mainPagination.allSections.length === 0)) ||
        (!hasAnyData && loading)

    // Determine if there is a next page available across any source
    const hasNextPage = useMemo(() => {
        if (currentSource === DataSource.MAIN) {
            return mainPagination.hasNextPage || holiduPagination.hasNextPage
        } // Can switch to holidu
        if (currentSource === DataSource.HOLIDU) {
            return holiduPagination.hasNextPage
        } // Holidu is the last source
        return false
    }, [currentSource, mainPagination.hasNextPage, holiduPagination.hasNextPage])

    const error = paginations[currentSource].error || null

    /**
     * Resets the state for a completely new search/filter
     */
    const reset = useCallback(() => {
        committedFilterRef.current = filter
        currentSourceRef.current = DataSource.MAIN
        setCurrentSource(DataSource.MAIN)
        isLoadingRef.current = false
        initializedRef.current = false
        autoSwitchUsedRef.current = {
            [DataSource.MAIN]: false,
            [DataSource.HOLIDU]: false,
        }

        paginationsRef.current[DataSource.MAIN].reset()
        paginationsRef.current[DataSource.HOLIDU].reset()
        primaryHeadingRef.current = ''
        srlNoResultRef.current = undefined
    }, [filter])

    // Reset and fetch when dependencies change (e.g., filter string)
    useEffect(() => {
        reset()
    }, [filter, reset])

    // Auto-fetch main listings when in initial/reset state
    useABEffect(() => {
        const main = paginations[DataSource.MAIN]
        if (
            currentSource === DataSource.MAIN &&
            main.currentPage === 0 &&
            main.allSections.length === 0 &&
            !main.loading &&
            !main.error
        ) {
            main.nextPage()
        }
    }, [currentSource, paginations])

    return {
        sections,
        primaryTotalCount,
        primaryHeading: primaryHeadingRef.current,
        srlNoResult: srlNoResultRef.current,
        loading,
        isInitializing,
        hasNextPage,
        loadMore,
        reset,
        currentSource,
        error,
    }
}

export default useHybridListings
